/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { VisuallyHidden } from '@coursera/cds-core';
import type { CSSProperties } from '@coursera/coursera-ui';
import { Button, Col, Row, StyleSheet, breakPoint, color, css } from '@coursera/coursera-ui';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import Container from 'bundles/xddp/components/shared/Container';

export type AtAGlanceItemProps = {
  title?: Maybe<string> | JSX.Element;
  subtitle?: Maybe<string>;
  detailsTitle?: Maybe<string>;
  details?: Maybe<string>;
  screenReaderPrefix?: string;
  sectionScreenReaderTitle?: string;
  columnSize?: 3 | 4;
};

export type AtAGlanceDisplayListType =
  | [AtAGlanceItemProps, AtAGlanceItemProps, AtAGlanceItemProps]
  | [AtAGlanceItemProps, AtAGlanceItemProps, AtAGlanceItemProps, AtAGlanceItemProps];

type Props = {
  displayList: AtAGlanceDisplayListType | null | undefined;
  sectionScreenReaderTitle?: string;
};

const cuiStyles = StyleSheet.create({
  atAGlanceRow: {
    border: '1px solid lightgray',
    borderRadius: '5px',
    ':nth-child(n) > div': {
      borderRight: '1px solid lightgray',
      [`@media (max-width: ${breakPoint.md - 1}px)`]: {
        borderRight: 'none',
        borderBottom: '1px solid lightgray',
        marginLeft: '2rem',
        marginRight: '2rem',
      },
    },
    ':nth-child(n) > div:last-of-type': {
      borderRight: 'none',
      borderBottom: 'none',
    },
  },
});

const styles: Record<string, CSSProperties> = {
  atAGlanceSectionWrapper: {
    margin: '0 auto',
    padding: '3.25rem 0',
    backgroundColor: '#fff',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '3rem 1.5rem',
    },
  },
  atAGlanceItem: {
    padding: '1.5rem 1.875rem',
    height: '100%',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '1.5rem 0',
    },
  },
  atAGlanceTitle: {
    fontFamily: 'inherit',
    color: `${color.primaryText}`,
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      marginBottom: '0.375rem',
    },
  },
  atAGlanceSubtitle: {
    fontFamily: 'inherit',
    color: '#515154',
    fontSize: '0.875rem',
    lineHeight: '1rem',
    marginBottom: 0,
  },
  atAGlanceButton: {
    button: {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
    },
  },
  atAGlanceToolTip: {
    whiteSpace: 'pre-line',
  },
};

export const AtAGlanceItem = ({
  title,
  subtitle,
  detailsTitle,
  details,
  columnSize,
  screenReaderPrefix,
  sectionScreenReaderTitle,
}: AtAGlanceItemProps) => {
  const Htag = sectionScreenReaderTitle ? 'h3' : 'h2';
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Col md={columnSize} noGutter data-test="AtAGlanceItem">
      <div css={styles.atAGlanceItem}>
        {title && (
          <Htag css={styles.atAGlanceTitle} data-test="at-a-glance-title">
            {screenReaderPrefix && (
              <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
                {screenReaderPrefix}
              </VisuallyHidden>
            )}
            {title}
          </Htag>
        )}
        {subtitle && (
          <p css={styles.atAGlanceSubtitle} data-test="at-a-glance-subtitle">
            {subtitle}
          </p>
        )}
        {details && (
          <TooltipWrapper
            placement="bottom"
            message={
              <div id="tooltip-text" css={styles.atAGlanceToolTip}>
                {details}
              </div>
            }
          >
            <span
              onMouseEnter={() => setIsTooltipOpen(true)}
              onMouseLeave={() => setIsTooltipOpen(false)}
              onFocus={() => setIsTooltipOpen(true)}
              onBlur={() => setIsTooltipOpen(false)}
              css={styles.atAGlanceButton}
            >
              <Button
                type="link"
                size="zero"
                data-test="at-a-glance-button"
                style={{ backgroundColor: '#fff' }}
                htmlAttributes={{
                  'aria-describedby': 'tooltip-text',
                  'aria-expanded': isTooltipOpen,
                  'aria-label': `${detailsTitle}. ${details}`,
                }}
              >
                {detailsTitle}
              </Button>
            </span>
          </TooltipWrapper>
        )}
      </div>
    </Col>
  );
};

const AtAGlanceSection = ({ sectionScreenReaderTitle, displayList }: Props) => {
  if (!displayList) {
    return null;
  }
  const columnSize = displayList && displayList.length === 3 ? 4 : 3;

  return (
    <div data-test="AtAGlanceSection" css={styles.atAGlanceSectionWrapper}>
      {sectionScreenReaderTitle && (
        <VisuallyHidden component="h2" className="rc-A11yScreenReaderOnly">
          {sectionScreenReaderTitle}
        </VisuallyHidden>
      )}
      <Container>
        <Row rootClassName={css(cuiStyles.atAGlanceRow).className}>
          {displayList?.map((itemProps) => (
            <AtAGlanceItem {...itemProps} columnSize={columnSize} />
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default AtAGlanceSection;
